/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-empty-interface */
import {
  types,
  SnapshotOut,
  Instance,
  getRoot,
  getSnapshot,
  IAnyModelType,
  tryReference,
} from "mobx-state-tree";

import { RootStore } from "..";
import { CourseLinkModel } from "./CourseLink";
import { ContentImageModel } from "../ContentImage/ContentImage";
import { KnowledgeLevelModel } from "../KnowledgeLevel/KnowledgeLevel";
import { Lesson } from "../Lesson/LessonModel";
import { Passing } from "../Passing/Passing";
import { Unit } from "../Unit/UnitModel";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LANGUAGE_CODES = {
  "1": "en",
  "6": "tt",
} as const;

type LanguageCode = (typeof LANGUAGE_CODES)[keyof typeof LANGUAGE_CODES];

export const CourseModel = types
  .model("CourseModel", {
    id: types.identifier,
    name: types.maybeNull(types.string),
    description: types.maybeNull(types.string),
    learningCourseLinks: types.optional(types.array(CourseLinkModel), []),
    isForTesting: types.optional(types.boolean, false), // Для тестового курса приходят определенные пассинки, с припиской Test и ограничивается продвижение по курсу
    lineups: types.optional(types.array(types.string), []),

    // Использюется только в одном месте ModalTestResult
    maxMark: types.maybeNull(types.number),
    code: types.maybeNull(types.string),

    // Используется только для показа всех курсов в джетклассе, нужно понять чем отличается от questionaries_knowledge_level
    knowledgeLevel: types.maybeNull(
      types.reference(types.late((): IAnyModelType => KnowledgeLevelModel))
    ),

    startDate: types.maybeNull(types.Date),

    // Используется только в упражнениях
    // TODO Удалить после перехода на новые контентные ручки
    languageId: types.maybeNull(types.string),
    contentImages: types.optional(types.array(ContentImageModel), []), // Отсюда берутся все картинки для курса, нужно заменить на новые поля (pictureUrl и pictureUrlLongForExercise)
  })
  .views((self) => ({
    getUnits(): Unit[] {
      const rootStore = getRoot<RootStore>(self);
      const units = rootStore.unit.items.filter((unit) => {
        // Возможно курс ещё не подгрузился поэтому нужно проверять ссылку
        const course = tryReference(() => unit?.course);

        if (course) {
          return unit?.course?.id === self.id;
        }

        return false;
      });
      return units;
    },
    get groupStatus(): string | null | undefined {
      const { learningGroup } = getRoot<RootStore>(self);
      const group = learningGroup.items.find((group) =>
        group.courses.find((course: { id: string }) => course.id === self.id)
      );
      return group?.status;
    },
    get lessons(): Lesson[] {
      return getRoot<RootStore>(self).lesson.items.filter(
        (lesson) => lesson?.course?.id === self.id
      );
    },
    get passing(): Passing | undefined {
      return getRoot<RootStore>(self).passing.items.find(
        (passing) => getSnapshot(passing)?.course === self.id
      );
    },
    get hasGroup(): boolean {
      const { learningGroup } = getRoot<RootStore>(self);
      const group = learningGroup.items.find((group) =>
        group.courses.find((course: { id: string }) => course.id === self.id)
      );
      return !!group;
    },
    get languageCode(): LanguageCode {
      const rootStore = getRoot<RootStore>(self);
      return (rootStore.language.getById(self.languageId ?? "1")?.code ??
        "en") as LanguageCode;
    },
    get lineupId(): string | undefined {
      return self.lineups[0];
    },
  }))
  .views((self) => ({
    get sortedUnits(): Unit[] {
      return self.getUnits().sort((a, b) => Number(a.order) - Number(b.order));
    },
    get totalProgress(): number {
      return (
        (self.isForTesting
          ? self.passing?.passingTestPercent
          : self.passing?.passingPercent) ?? 0
      );
    },
    get resultProgress(): number {
      return (
        (self.isForTesting
          ? self.passing?.markTestPercent
          : self.passing?.markPercent) ?? 0
      );
    },
  }))
  .views((self) => ({
    get firstUnit(): Unit {
      return self.sortedUnits[0];
    },
    get lastUnit(): Unit {
      return self.sortedUnits[self.sortedUnits.length - 1];
    },
    getNextUnit(unitId: string): Unit | null {
      const index = self.sortedUnits.findIndex(
        ({ id }) => id === unitId
      ) as number;

      return self.sortedUnits[index + 1] ?? null;
    },
  }))
  .views((self) => ({
    get classesProgress(): { completed: number; total: number } {
      const lessonsCompleted = self.lessons.filter(
        (lesson) => lesson.isCompleted && lesson.compensation !== true
      );
      const lessonsIsNotCompleted = self.lessons.filter(
        (lesson) =>
          lesson.currentStatus === "planned" && lesson.compensation !== true
      );
      const totalLessonsCount =
        lessonsIsNotCompleted.length + lessonsCompleted.length;
      return { completed: lessonsCompleted.length, total: totalLessonsCount };
    },
    get classesTimeProgress(): { completed: number; total: number } {
      const allLessons = self.lessons.filter(
        ({ compensation }) => compensation !== true
      );
      const completedLessons = allLessons.filter(
        ({ isCompleted }) => isCompleted
      );
      const plannedLessons = self.lessons.filter(
        ({ currentStatus }) => currentStatus === "planned"
      );
      const totalLessons = [...completedLessons, ...plannedLessons];

      const completedTime = completedLessons.reduce(
        (time, { duration }) => time + duration,
        0
      );
      const totalTime = totalLessons.reduce(
        (time, { duration }) => time + duration,
        0
      );
      return { completed: completedTime, total: totalTime };
    },
    get contentImageLongForExercise() {
      return self.contentImages.find((image) => image.isLongForExercise);
    },
    get contentImageForPreview() {
      return self.contentImages.find((image) => image.isForPreview);
    },
  }));

type CourseType = Instance<typeof CourseModel>;
export type Course = CourseType;
type CourseSnapshotType = SnapshotOut<typeof CourseModel>;
export type CourseSnapshot = CourseSnapshotType;
